import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import AddressDialog from "../../lib/components/app/AddressDialog";
import Icon from "../../lib/components/custom/Icon";
import { RestaurantClosedBanner } from "../../lib/components/custom/RestaurantClosedBanner";
import { Badge } from "../../lib/components/ui/badge";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { Separator } from "../../lib/components/ui/separator";
import { Skeleton } from "../../lib/components/ui/skeleton";
import { calculateExtrasTotal, getExtraPrice } from "../../lib/constants";
import {
	getNextOpenTime,
	isRestaurantOpen,
} from "../../lib/constants/restaurant-hours";
import { useApi } from "../../lib/hooks/api/use-api";
import { useOrderFlowStore } from "../../lib/hooks/use-store";
import type { GetTypes } from "../../lib/types/api-types";
import { calculateOnlinePaymentFee } from "../../lib/utils/pricing";
import Layout from "../layout";

export default function Checkout() {
	const { t } = useTranslation();
	const { auth, cart, users } = useApi();
	const { selectedAddressId, setSelectedAddressId } = useOrderFlowStore();
	const navigate = useNavigate();

	const { data: session } = auth.useSession();
	const {
		data: cartData,
		isLoading: cartLoading,
		error: cartError,
		refetch: refetchCart,
	} = cart.getByUserId(session?.user?.id || "");
	const { data: addressesData, isLoading: addressesLoading } =
		users.addresses.getAll();
	const { data: deliveryZones } = users.addresses.getZones();

	// Delivery information state (declare before calculations)
	const [deliveryInfo, setDeliveryInfo] = useState<{
		fee: number;
		estimatedTime: number;
		minimumOrder: number;
		zoneName?: string;
		isValid: boolean;
	}>({
		fee: 0,
		estimatedTime: 30,
		minimumOrder: 0,
		isValid: false,
	});

	const items = cartData?.cartItems || [];
	// Sort addresses with default first, then by created date
	const addresses = useMemo(() => {
		return (
			addressesData?.addresses?.slice().sort((a, b) => {
				// Default addresses always come first
				if (a.isDefault && !b.isDefault) return -1;
				if (!a.isDefault && b.isDefault) return 1;
				// Then sort by createdAt (most recent first)
				const aDate = new Date(a.createdAt || 0).getTime();
				const bDate = new Date(b.createdAt || 0).getTime();
				return bDate - aDate;
			}) || []
		);
	}, [addressesData?.addresses]);
	const itemCount = items.length;

	// Calculate totals from API data (including extras)
	const subtotal = items.reduce((sum: number, item) => {
		const basePrice = parseFloat(item.menuSize.price) * item.quantity;
		const extrasPrice =
			calculateExtrasTotal(item.extras || [], item.menuSize.sizeName) *
			item.quantity;
		return sum + basePrice + extrasPrice;
	}, 0);
	const deliveryFee = deliveryInfo.fee;
	const total = subtotal + deliveryFee;
	const estimatedOnlineFee = calculateOnlinePaymentFee(total);

	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

	// Restaurant hours check
	const restaurantOpen = isRestaurantOpen();
	const nextOpenTime = getNextOpenTime();

	// Auto-select default address when addresses load
	useEffect(() => {
		if (addresses.length > 0 && !selectedAddressId) {
			const defaultAddress = addresses.find((addr) => addr.isDefault);
			if (defaultAddress) {
				setSelectedAddressId(defaultAddress.id);
			}
		}
	}, [addresses, selectedAddressId, setSelectedAddressId]);

	// Calculate delivery info when selected address or zones change
	const selectedAddress = useMemo(() => {
		return addresses.find((addr) => addr.id === selectedAddressId);
	}, [addresses, selectedAddressId]);

	const { mutate: validateAddress } = users.addresses.validateAddress;

	useEffect(() => {
		if (!selectedAddress?.id || !deliveryZones?.zones) {
			setDeliveryInfo((prev) =>
				prev.isValid
					? {
							fee: 0,
							estimatedTime: 30,
							minimumOrder: 0,
							isValid: false,
						}
					: prev,
			);
			return;
		}

		// Validate address against delivery zones
		validateAddress(
			{
				streetAddress: selectedAddress.streetAddress || "",
				city: selectedAddress.city || "",
				zipCode: selectedAddress.zipCode || "",
				country: selectedAddress.country || "Germany",
			},
			{
				onSuccess: (result) => {
					if (result.isValid) {
						setDeliveryInfo({
							fee: result.deliveryFee,
							estimatedTime: result.estimatedDeliveryTime,
							minimumOrder: result.minimumOrder,
							zoneName: result.zone.name,
							isValid: true,
						});
					} else {
						setDeliveryInfo({
							fee: 0,
							estimatedTime: 30,
							minimumOrder: 0,
							isValid: false,
						});
					}
				},
				onError: () => {
					setDeliveryInfo({
						fee: 0,
						estimatedTime: 30,
						minimumOrder: 0,
						isValid: false,
					});
				},
			},
		);
	}, [
		selectedAddress?.id,
		selectedAddress?.streetAddress,
		selectedAddress?.zipCode,
		selectedAddress?.city,
		selectedAddress?.country,
		deliveryZones?.zones,
		validateAddress,
	]);

	// Type for address (used in state/props)
	type Address = GetTypes["users"]["addresses"]["getAll"] extends {
		addresses?: infer Addrs;
	}
		? Addrs extends (infer Addr)[]
			? Addr
			: never
		: never;
	const [editingAddress, setEditingAddress] = useState<Address | null>(null);

	const handleCreateAddressSuccess = (newAddress: { address: Address }) => {
		setSelectedAddressId(newAddress.address.id);
	};

	const openEditDialog = (address: Address) => {
		setEditingAddress(address);
	};

	const handleContinue = () => {
		if (!session?.user?.id) {
			toast.error("Please sign in to continue with checkout");
			navigate({ to: "/auth/signin" });
			return;
		}

		if (!selectedAddressId) {
			toast.error("Please select a delivery address");
			return;
		}

		// Find the selected address
		const selectedAddress = addresses.find(
			(addr) => addr.id === selectedAddressId,
		);
		if (!selectedAddress) {
			toast.error(t("checkout.errors.select_address"));
			return;
		}

		// Check minimum order requirement
		if (subtotal < deliveryInfo.minimumOrder) {
			toast.error(
				`Minimum order for ${deliveryInfo.zoneName} is €${deliveryInfo.minimumOrder.toFixed(2)}. Your current order is €${subtotal.toFixed(2)}.`,
			);
			return;
		}

		// Check if delivery area is valid
		if (!deliveryInfo.isValid) {
			toast.error("Delivery not available for the selected address");
			return;
		}

		// Store selected address ID in the order flow store
		setSelectedAddressId(selectedAddressId);
		navigate({ to: "/review" });
	};

	// Authentication check
	if (!session?.user?.id) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full flex items-center justify-center">
					<div className="max-w-2xl mx-auto text-center">
						<Card className="p-12">
							<div className="flex flex-col items-center gap-4">
								<Icon
									name="LogIn"
									className="w-16 h-16 text-muted-foreground"
								/>
								<div>
									<h2 className="text-xl font-semibold mb-2">
										{t("checkout.sign_in_required.header")}
									</h2>
									<p className="text-muted-foreground mb-6">
										{t("checkout.sign_in_required.subheader")}
									</p>
									<Button asChild>
										<Link to="/auth/signin">
											{t("checkout.sign_in_required.button")}
										</Link>
									</Button>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	// Loading state
	if (cartLoading || addressesLoading) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full">
					<div className="max-w-4xl mx-auto">
						{/* Header Skeleton */}
						<div className="mb-8">
							<Skeleton className="h-8 w-48 mb-2" />
							<Skeleton className="h-4 w-64" />
						</div>

						<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
							{/* Form Skeleton */}
							<div className="lg:col-span-2 space-y-6">
								<Card className="p-6">
									<Skeleton className="h-6 w-48 mb-6" />
									<div className="space-y-4">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<Skeleton className="h-10 w-full" />
											<Skeleton className="h-10 w-full" />
										</div>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<Skeleton className="h-10 w-full" />
											<Skeleton className="h-10 w-full" />
										</div>
										<Skeleton className="h-10 w-full" />
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<Skeleton className="h-10 w-full" />
											<Skeleton className="h-10 w-full" />
											<Skeleton className="h-10 w-full" />
										</div>
										<Skeleton className="h-24 w-full" />
									</div>
								</Card>
							</div>

							{/* Summary Skeleton */}
							<Card className="p-6">
								<Skeleton className="h-6 w-32 mb-4" />
								<div className="space-y-3 mb-6">
									{Array.from({ length: 3 }).map((_, i) => (
										<Skeleton key={i} className="h-4 w-full" />
									))}
								</div>
								<Skeleton className="h-10 w-full" />
							</Card>
						</div>
					</div>
				</div>
			</Layout>
		);
	}

	// Error state
	if (cartError) {
		return (
			<Layout>
				<div className="p-8 border rounded-large bg-gradient-main min-h-full flex items-center justify-center">
					<div className="max-w-2xl mx-auto text-center">
						<Card className="p-12">
							<div className="flex flex-col items-center gap-4">
								<Icon
									name="CircleAlert"
									className="w-16 h-16 text-destructive"
								/>
								<div>
									<h2 className="text-xl font-semibold mb-2">
										{t("cart.error.header")}
									</h2>
									<p className="text-muted-foreground mb-6">
										{t("cart.error.subheader")}
									</p>
									<Button onClick={() => refetchCart()}>
										{t("cart.error.retry")}
									</Button>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	if (itemCount === 0) {
		return (
			<Layout>
				<div className="p-8 border min-h-full rounded-large bg-gradient-main flex items-center justify-center">
					<div className="max-w-2xl mx-auto text-center">
						<Card className="p-12">
							<div className="flex flex-col items-center gap-4">
								<Icon
									name="ShoppingCart"
									className="w-16 h-16 text-muted-foreground"
								/>
								<div>
									<h2 className="text-xl font-semibold mb-2">
										{t("checkout.empty_cart.header")}
									</h2>
									<p className="text-muted-foreground mb-6">
										{t("checkout.empty_cart.subheader")}
									</p>
									<Button asChild>
										<Link to="/">{t("checkout.empty_cart.browse_menu")}</Link>
									</Button>
								</div>
							</div>
						</Card>
					</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="p-6 md:p-8 border rounded-large bg-gradient-main min-h-full">
				<div className="max-w-4xl md:max-w-6xl mx-auto">
					{/* Header */}
					<div className="mb-8">
						<div className="flex items-center gap-4">
							<Button
								variant="ghost"
								size="sm"
								asChild
								className="p-0 h-auto hover:bg-transparent"
							>
								<Link
									to="/cart"
									className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
								>
									<Icon name="ArrowLeft" className="w-5 h-5" />
								</Link>
							</Button>
							<div>
								<h1 className="text-3xl font-manrope font-semibold">
									{t("checkout.header")}
								</h1>
								<p className="text-muted-foreground mt-1">
									{t("checkout.subheader")}
								</p>
							</div>
						</div>
					</div>

					{/* Restaurant Closed Banner */}
					{!restaurantOpen && (
						<div className="mb-6">
							<RestaurantClosedBanner nextOpenTime={nextOpenTime} />
						</div>
					)}

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
						{/* Delivery Address Selection */}
						<div className="lg:col-span-2 space-y-6">
							<Card className="p-6">
								<div className="flex items-center justify-between mb-6">
									<h2 className="text-xl font-semibold">Delivery Address</h2>
									<AddressDialog
										mode="create"
										open={isCreateDialogOpen}
										onOpenChange={setIsCreateDialogOpen}
										onCreateSuccess={handleCreateAddressSuccess}
										phoneRequired={true}
										trigger={
											<Button variant="primary" size="sm">
												<Icon name="Plus" className="w-4 h-4" />
												Add Address
											</Button>
										}
									/>
								</div>

								{/* Edit Dialog */}
								<AddressDialog
									mode="edit"
									open={!!editingAddress}
									onOpenChange={(open) => !open && setEditingAddress(null)}
									address={editingAddress}
									onSuccess={() => setEditingAddress(null)}
									phoneRequired={true}
								/>

								{/* Address Selection */}
								{addresses.length === 0 ? (
									<div className="text-center py-8">
										<div className="w-16 h-16 bg-primary/10 rounded-full mx-auto flex items-center justify-center mb-4">
											<Icon name="MapPin" className="w-8 h-8 text-primary" />
										</div>
										<h3 className="text-lg font-semibold mb-2">
											{t("checkout.delivery_address.no_addresses")}
										</h3>
										<p className="text-muted-foreground mb-4">
											{t("checkout.delivery_address.add_new")}
										</p>
										<Button
											variant="primary"
											onClick={() => setIsCreateDialogOpen(true)}
										>
											<Icon name="Plus" className="w-4 h-4" />
											{t("checkout.delivery_address.add_new")}
										</Button>
									</div>
								) : (
									<div className="space-y-4">
										{addresses.map((address) => (
											<div
												key={address.id}
												className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 relative ${
													selectedAddressId === address.id
														? "border-primary bg-primary/5 shadow-sm"
														: "border-border hover:border-primary/50 hover:bg-accent/50"
												}`}
												onClick={() => setSelectedAddressId(address.id)}
												tabIndex={0}
												role="button"
												onKeyDown={(e) => {
													if (e.key === "Enter" || e.key === "Space") {
														setSelectedAddressId(address.id);
													}
												}}
											>
												{/* Edit Button */}
												<Button
													variant="ghost"
													size="sm"
													className="absolute top-3 right-3 h-8 w-8 p-0 hover:bg-background/80"
													onClick={(e) => {
														e.stopPropagation();
														openEditDialog(address);
													}}
												>
													<Icon name="Pencil" className="w-4 h-4" />
												</Button>

												<div className="flex items-start gap-3">
													<div
														className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-1 shrink-0 ${
															selectedAddressId === address.id
																? "border-primary bg-primary"
																: "border-muted-foreground"
														}`}
													>
														{selectedAddressId === address.id && (
															<div className="w-2 h-2 rounded-full bg-white" />
														)}
													</div>
													<div className="flex-1 pr-10 text-left">
														<div className="flex items-center gap-2 mb-2">
															<h3 className="font-semibold">{address.label}</h3>
															{address.isDefault && (
																<Badge variant="secondary" className="text-xs">
																	Default
																</Badge>
															)}
														</div>
														<div className="text-sm text-muted-foreground space-y-1">
															<p className="font-medium text-foreground">
																{address.streetAddress}
															</p>
															<p>
																{address.city}, {address.state}{" "}
																{address.zipCode}
															</p>
															{address.phone && <p>Phone: {address.phone}</p>}
															{address.instructions && (
																<p className="italic">
																	"{address.instructions}"
																</p>
															)}
														</div>
													</div>
												</div>
											</div>
										))}
									</div>
								)}
							</Card>
						</div>

						{/* Order Summary */}
						<div className="lg:col-span-1">
							<Card className="p-6 sticky top-4 ">
								<h2 className="text-xl font-semibold mb-4">Order Summary</h2>

								<div className="space-y-3 mb-6">
									{items.map((item) => {
										const basePrice =
											parseFloat(item.menuSize.price) * item.quantity;
										const extrasPrice =
											calculateExtrasTotal(
												item.extras || [],
												item.menuSize.sizeName,
											) * item.quantity;
										const itemTotal = basePrice + extrasPrice;

										return (
											<div key={item.id} className="space-y-1">
												<div className="flex justify-between text-sm">
													<span className="font-medium">
														{item.menuItem.name} ({item.menuSize.sizeName}) x
														{item.quantity}
													</span>
													<span className="font-medium">
														€{itemTotal.toFixed(2)}
													</span>
												</div>
												{item.extras && item.extras.length > 0 && (
													<div className="flex flex-wrap gap-1 pl-2">
														{item.extras?.map((extra, idx: number) => {
															const extraPrice = getExtraPrice(
																extra,
																item.menuSize.sizeName,
															);
															return (
																<Badge
																	key={`${extra.id}-${idx}`}
																	variant="secondary"
																	className="text-xs px-1.5 py-0"
																>
																	{extra.name}
																	{extraPrice > 0 &&
																		` +€${extraPrice.toFixed(2)}`}
																	{extra.quantity > 1 && ` x${extra.quantity}`}
																</Badge>
															);
														})}
													</div>
												)}
											</div>
										);
									})}
								</div>

								<Separator className="mb-4" />

								<div className="space-y-3 mb-6">
									<div className="flex justify-between">
										<span>Subtotal</span>
										<span>€{subtotal.toFixed(2)}</span>
									</div>
									<div className="flex justify-between">
										<span>Delivery</span>
										<span className={deliveryFee === 0 ? "text-primary" : ""}>
											{deliveryFee === 0 ? "NA" : `€${deliveryFee.toFixed(2)}`}
										</span>
									</div>
									<div className="flex justify-between text-sm text-muted-foreground">
										<span>Online service fee (card)</span>
										<span>€{estimatedOnlineFee.toFixed(2)}</span>
									</div>
									{deliveryInfo.zoneName && (
										<div className="flex justify-between text-sm text-muted-foreground">
											<span>Delivery Zone</span>
											<span>{deliveryInfo.zoneName}</span>
										</div>
									)}
									{deliveryInfo.minimumOrder > 0 && (
										<div className="flex justify-between text-sm text-muted-foreground">
											<span>Min. Order</span>
											<span>€{deliveryInfo.minimumOrder.toFixed(2)}</span>
										</div>
									)}
								</div>

								<Separator className="mb-4" />

								<div className="flex justify-between text-lg font-semibold mb-6">
									<span>Total</span>
									<span>€{(total + estimatedOnlineFee).toFixed(2)}</span>
								</div>

								<Button
									className="w-full"
									size="lg"
									onClick={handleContinue}
									variant="primary"
								>
									{t("checkout.place_order")}
									<Icon name="ChevronRight" className="w-4 h-4" />
								</Button>
							</Card>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
}
